// Zod schema definitions for all major data structures in the tech radar system.
// This file provides a single source of truth for config, data, and metadata validation.
//
// Usage example:
// import { RadarConfigSchema } from './zodSchemas'
// RadarConfigSchema.parse(configObject) // throws if invalid
//
// All schemas are used for:
// - Validating data on load, save, import, export, and migration
// - Ensuring backward compatibility during schema evolution
// - Exporting TypeScript types for use throughout the codebase

import { z } from 'zod'

export const CURRENT_SCHEMA_VERSION = 1

export const RingSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string(),
  color: z.string(),
  radius: z.number(),
  strokeWidth: z.number()
})

export const QuadrantSchema = z.object({
  id: z.string(),
  position: z.number(),
  title: z.string(),
  description: z.string(),
  color: z.string()
})

export const ChartSchema = z.object({
  size: z.number(),
  blipSize: z.number()
})

export const FlagConfigSchema = z.object({
  color: z.string(),
  title: z.string(),
  titleShort: z.string().optional(),
  description: z.string()
})

// RadarConfigSchema: Validates the structure of a radar's configuration object.
export const RadarConfigSchema = z.object({
  version: z.string(), // Add version as a required field
  basePath: z.string(),
  baseUrl: z.string(),
  logoFile: z.string(),
  logoSvg: z.string(), // SVG string for logo (frontend only)
  toggles: z.object({
    showChart: z.boolean(),
    showTagFilter: z.boolean(),
    showQuadrantList: z.boolean(),
    showEmptyRings: z.boolean(),
    enableEmbed: z.boolean().optional()
  }),
  sections: z.array(z.string()),
  colors: z.record(z.string(), z.any()),
  quadrants: z.array(QuadrantSchema),
  rings: z.array(RingSchema),
  flags: z.record(z.string(), FlagConfigSchema),
  chart: ChartSchema,
  labels: z.record(z.string(), z.string())
})

// ItemReleaseSchema: Validates a single release entry for a radar item.
export const ItemReleaseSchema = z.object({
  id: z.string(),
  title: z.string(),
  ring: z.string(),
  body: z.string(),
  flag: z.string(), // Should match the Flag enum in types.ts
  releaseDate: z.string(),
  position: z.tuple([z.number(), z.number()])
})

// ItemSchema: Validates a single radar item, including its releases.
export const ItemSchema = z.object({
  id: z.string(),
  quadrant: z.string(),
  tags: z.array(z.string()),
  releases: z.array(ItemReleaseSchema),
  archived: z.boolean().optional()
})

// RadarMetadataSchema: Validates metadata for a single radar (excluding schemaVersion).
export const RadarMetadataSchema = z.object({
  id: z.number(),
  name: z.string(),
  config: RadarConfigSchema
})

// AllRadarMetadataSchema: Validates the full metadata object for all radars.
export const AllRadarMetadataSchema = z.object({
  schemaVersion: z.number(),
  radars: z.record(z.string(), RadarMetadataSchema)
})

// RadarDataSchema: Validates the full data object for a radar.
export const RadarDataSchema = z.object({
  items: z.array(ItemSchema)
})

export const RadarDataStorageSchema = z.object({
  schemaVersion: z.number(),
  data: RadarDataSchema
})

// WebhookSecretSchema: Validates webhook secret storage
export const WebhookSecretSchema = z.union([
  z.literal(''), // allow empty string (no secret)
  z
    .string()
    .min(1)
    .max(128)
    .regex(/^[A-Za-z0-9\-_~.]+$/, 'Secret must be URL-safe (A-Za-z0-9-_~.)')
])

// UserSettingsSchema: Validates user-specific settings
export const UserSettingsSchema = z.object({
  emailProvided: z.boolean().optional().default(false),
  emailPromptDismissed: z.boolean().optional().default(false),
  lastEmailPromptDate: z.number().optional() // timestamp
})

// SystemSettingsSchema: Validates system-wide settings including user settings
export const SystemSettingsSchema = z.object({
  schemaVersion: z.number().default(CURRENT_SCHEMA_VERSION),
  users: z.record(z.string(), UserSettingsSchema).default({})
})

// Export types for use throughout the codebase
export type RadarConfig = z.infer<typeof RadarConfigSchema>
export type ItemRelease = z.infer<typeof ItemReleaseSchema>
export type Item = z.infer<typeof ItemSchema>
export type RadarData = z.infer<typeof RadarDataSchema>
export type RadarMetadata = z.infer<typeof RadarMetadataSchema>
export type AllRadarMetadata = z.infer<typeof AllRadarMetadataSchema>
export type WebhookSecret = z.infer<typeof WebhookSecretSchema>
export type UserSettings = z.infer<typeof UserSettingsSchema>
export type SystemSettings = z.infer<typeof SystemSettingsSchema>
